import React, { useState, useEffect } from 'react';
import { Calculator, ChevronDown, ChevronUp, HelpCircle, MapPin, Lightbulb } from 'lucide-react';
import { formatCurrency } from '../utils/helpers';
import SatMeasureModal from './jobs/SatMeasureModal';
import { getFriendlyLabel } from '../config/industryTemplates';
import { parseAttributes, getPropertyMeasureTarget, isStoryModifier } from '../config/assetTemplates';

/**
 * QuoteCalculator - Inline calculator for job form
 * Uses ServiceType pricing configuration to calculate quotes
 * 
 * Now includes SatMeasure integration for satellite-based property measurement
 * v2: Added "Round to" quick buttons with price override for exact round amounts
 * v3: Added friendly label display when measurement is entered/captured
 */
const QuoteCalculator = ({ 
  serviceType, 
  onQuoteCalculated,
  initialValues = {},
  className = '',
  // Props for SatMeasure integration
  customerAddress = '',
  customerName = '',
  // Property link: the selected Property record and a save function
  property = null,
  onSaveToProperty = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [measurementValue, setMeasurementValue] = useState(initialValues.measurementValue || '');
  const [selectedTier, setSelectedTier] = useState(initialValues.selectedTier || '');
  const [activeModifiers, setActiveModifiers] = useState(initialValues.activeModifiers || {});
  const [calculatedQuote, setCalculatedQuote] = useState(null);
  
  // Price override - when user clicks "Round to", this overrides the calculated total
  // Also restore from initialValues when editing a job
  const [priceOverride, setPriceOverride] = useState(initialValues.priceOverride || null);
  
  // Override price stays tucked away unless one is already set
  const [showOverride, setShowOverride] = useState(!!initialValues.priceOverride);

  // SatMeasure modal state
  const [showSatMeasure, setShowSatMeasure] = useState(false);
  const [measurementSource, setMeasurementSource] = useState(null); // null | 'manual' | 'satmeasure'
  
  // Friendly label state - matched from measurement
  const [matchedFriendlyLabel, setMatchedFriendlyLabel] = useState(null);

  // Parse JSON fields
  const modifiers = serviceType?.modifiersJson ? JSON.parse(serviceType.modifiersJson) : [];
  const tiers = serviceType?.tiersJson ? JSON.parse(serviceType.tiersJson) : [];
  const friendlyLabels = serviceType?.friendlyLabelsJson ? JSON.parse(serviceType.friendlyLabelsJson) : null;

    // ---- Property link (sq ft / stories) ----
  const tplName = serviceType?.pricingTemplate?.toLowerCase() || '';
  const isAreaTemplate = ['persqft', 'per-sqft', 'perlinearft', 'per-linear-ft'].includes(tplName);
  const propAttrs = property ? parseAttributes(property.attributesJson) : {};
  const propTarget = property && isAreaTemplate
    ? getPropertyMeasureTarget(serviceType?.name, tplName.includes('linear') ? 'linear' : 'sqft')
    : null;
  const propValue = propTarget && Number(propAttrs[propTarget.key]) > 0
    ? Math.round(Number(propAttrs[propTarget.key]))
    : null;
  const propSource = propTarget ? propAttrs[`${propTarget.key}Source`] : null;
  const propDate = propTarget ? propAttrs[`${propTarget.key}UpdatedAt`] : null;
  const propStories = Number(propAttrs.stories) || null;
  const storyModifier = modifiers.find(m => m.enabled !== false && isStoryModifier(m.name)) || null;
  const [savingToProperty, setSavingToProperty] = useState(false);

  // New quotes only: fill the measurement from the property when nothing is entered yet
  useEffect(() => {
    if (propValue && !measurementValue && !initialValues.measurementValue) {
      setMeasurementValue(String(propValue));
      setMeasurementSource('property');
    }
  }, [propValue]);

  // New quotes only: pre-check the story modifier when the property is 2+ stories
  useEffect(() => {
    if (propStories >= 2 && storyModifier && !initialValues.measurementValue) {
      setActiveModifiers(prev =>
        prev[storyModifier.name] === undefined ? { ...prev, [storyModifier.name]: true } : prev
      );
    }
  }, [propStories]);

  // What could be saved back to the property. Only offered when the property has no size yet,
  // or when the value was typed over the saved one, so old jobs never suggest overwriting newer data.
  const pendingUpdates = {};
  if (property && onSaveToProperty) {
    const typedValue = Math.round(parseFloat(measurementValue) || 0);
    if (propTarget && typedValue > 0 && typedValue !== propValue &&
        (propValue === null || measurementSource === 'manual')) {
      pendingUpdates[propTarget.key] = typedValue;
    }
    if (storyModifier && activeModifiers[storyModifier.name] && !(propStories >= 2)) {
      pendingUpdates.stories = 2;
    }
  }
  const pendingLabel = [
    propTarget && pendingUpdates[propTarget.key]
      ? `${pendingUpdates[propTarget.key].toLocaleString()} ${propTarget.unit}`
      : null,
    pendingUpdates.stories ? '2 stories' : null,
  ].filter(Boolean).join(' and ');

  const handleSavePending = async () => {
    if (!onSaveToProperty || Object.keys(pendingUpdates).length === 0) return;
    setSavingToProperty(true);
    try {
      await onSaveToProperty(pendingUpdates, 'manual');
    } finally {
      setSavingToProperty(false);
    }
  };

  // Calculate quote whenever inputs change
  useEffect(() => {
    calculateQuote();
  }, [measurementValue, selectedTier, activeModifiers, serviceType, priceOverride]);

  // Update friendly label when measurement changes
  useEffect(() => {
    if (measurementValue && friendlyLabels && friendlyLabels.length > 0) {
      const label = getFriendlyLabel(parseFloat(measurementValue), friendlyLabels);
      setMatchedFriendlyLabel(label);
    } else {
      setMatchedFriendlyLabel(null);
    }
  }, [measurementValue, friendlyLabels]);

  // Clear price override when measurement changes manually
  const handleMeasurementChange = (e) => {
    setMeasurementValue(e.target.value);
    setMeasurementSource('manual');
    setPriceOverride(null); // Clear override when user types
  };

  const calculateQuote = () => {
    if (!serviceType?.pricingTemplate) {
      setCalculatedQuote(null);
      return;
    }

    let baseAmount = 0;
    let breakdown = [];

    // Normalize template for comparison
    const template = serviceType.pricingTemplate?.toLowerCase() || '';

    // Calculate based on pricing template
    switch (template) {
      case 'flat':
      case 'flat-rate':
        baseAmount = serviceType.defaultPrice || 0;
        breakdown.push({
          label: serviceType.name,
          amount: baseAmount,
          detail: 'Flat rate'
        });
        break;

      case 'persqft':
      case 'per-sqft':
      case 'perlinearft':
      case 'per-linear-ft':
      case 'peritem':
      case 'per-unit':
      case 'perhour':
      case 'per-hour':
        if (measurementValue && serviceType.baseRate) {
          const value = parseFloat(measurementValue) || 0;
          const rate = parseFloat(serviceType.baseRate) || 0;
          baseAmount = value * rate;
          
          const unitLabel = template.includes('sqft') || template.includes('sq') ? 'sq ft' :
                           template.includes('linear') ? 'linear ft' :
                           template.includes('unit') || template.includes('item') ? 'items' : 'hours';
          
          breakdown.push({
            label: serviceType.name,
            amount: priceOverride || baseAmount, // Use override if set
            detail: `${value.toLocaleString()} ${unitLabel} × $${rate.toFixed(4)}`
          });
        }
        break;

      case 'tiered':
        if (selectedTier && tiers.length > 0) {
          const tier = tiers.find(t => t.name === selectedTier);
          if (tier) {
            baseAmount = parseFloat(tier.price) || 0;
            breakdown.push({
              label: `${serviceType.name} - ${tier.name}`,
              amount: baseAmount,
              detail: tier.description || ''
            });
          }
        }
        break;

      default:
        break;
    }

    // Apply modifiers (only if no price override)
    let modifierTotal = 0;
    if (!priceOverride) {
      modifiers.forEach(mod => {
        if (activeModifiers[mod.name] && mod.enabled !== false) {
          let modAmount = 0;
          if (mod.type === 'flat') {
            modAmount = parseFloat(mod.amount) || 0;
          } else if (mod.type === 'percent') {
            modAmount = baseAmount * (parseFloat(mod.amount) / 100);
          }
          
          if (modAmount > 0) {
            modifierTotal += modAmount;
            breakdown.push({
              label: mod.name,
              amount: modAmount,
              detail: mod.type === 'percent' ? `+${mod.amount}%` : `+$${mod.amount}`
            });
          }
        }
      });
    }

    // Use price override if set, otherwise use calculated total
    let total = priceOverride || (baseAmount + modifierTotal);

    // Apply minimum charge (only if no price override)
    const minimum = parseFloat(serviceType.minimumCharge) || 0;
    let appliedMinimum = false;
    if (!priceOverride && minimum > 0 && total > 0 && total < minimum) {
      total = minimum;
      appliedMinimum = true;
    }

    // Only set if we have a calculated value
    if (total > 0 || (serviceType.pricingTemplate === 'Tiered' && selectedTier)) {
      const result = {
        total,
        breakdown,
        appliedMinimum,
        minimum,
        measurementValue: measurementValue ? parseFloat(measurementValue) : null,
        measurementLabel: matchedFriendlyLabel, // Include the friendly label
        selectedTier,
        activeModifiers,
        measurementSource,
        priceOverride, // Include so parent knows this is a rounded price
      };
      
      setCalculatedQuote(result);
      
      // Notify parent
      if (onQuoteCalculated) {
        onQuoteCalculated(result);
      }
    } else {
      setCalculatedQuote(null);
      if (onQuoteCalculated) {
        onQuoteCalculated(null);
      }
    }
  };

  const handleModifierToggle = (modifierName) => {
    setActiveModifiers(prev => ({
      ...prev,
      [modifierName]: !prev[modifierName]
    }));
    setPriceOverride(null); // Clear override when modifiers change
  };

  // Handle measurement from SatMeasure
  const handleSatMeasureComplete = (result) => {
    if (result && result.value > 0) {
      setMeasurementValue(Math.round(result.value).toString());
      setMeasurementSource('satmeasure');
      setPriceOverride(null); // Clear override
      if (property && onSaveToProperty && propTarget) {
        onSaveToProperty({ [propTarget.key]: Math.round(result.value) }, 'measured');
      }
    }
    setShowSatMeasure(false);
  };

  // Generate round price options - round down and up to nearest $10
  const getRoundPriceOptions = () => {
    if (!calculatedQuote || !calculatedQuote.total || calculatedQuote.total <= 0) return [];
    
    // Don't show if already using a price override (already rounded)
    if (priceOverride) return [];
    
    const total = calculatedQuote.total;
    const options = [];
    
    // Round down to nearest $10
    const targetDown = Math.floor(total / 10) * 10;
    // Round up to nearest $10
    const targetUp = Math.ceil(total / 10) * 10;
    
    // Add round down option
    if (targetDown > 0 && targetDown < total) {
      options.push({
        price: targetDown,
        label: `$${targetDown}`
      });
    }
    
    // Add round up option (if different from down)
    if (targetUp > total && targetUp !== targetDown) {
      options.push({
        price: targetUp,
        label: `$${targetUp}`
      });
    }
    
    return options;
  };

  // Handle round to price click - sets price override
  const handleRoundToPrice = (option) => {
    setPriceOverride(option.price);
  };

  // Don't render if no pricing template configured
  if (!serviceType?.pricingTemplate) {
    return null;
  }

  // Support both legacy PascalCase and new lowercase-hyphenated formats
  const template = serviceType.pricingTemplate?.toLowerCase() || '';
  const needsMeasurement = ['persqft', 'per-sqft', 'perlinearft', 'per-linear-ft', 'peritem', 'per-unit', 'perhour', 'per-hour'].includes(template);
  const needsTierSelection = template === 'tiered';
  const hasModifiers = modifiers.length > 0 && !needsTierSelection;
  
  // Determine if SatMeasure is applicable (only for sqft and linear ft)
  const canUseSatMeasure = ['persqft', 'per-sqft', 'perlinearft', 'per-linear-ft'].includes(template);
  const measurementType = template.includes('linear') ? 'linear' : 'sqft';

  // Get round price options (only for measurement-based pricing)
  const roundPriceOptions = needsMeasurement ? getRoundPriceOptions() : [];

  return (
    <>
      <div className={`bg-purple-50 border border-purple-200 rounded-lg ${className}`}>
        {/* Header */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-purple-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Calculator size={18} className="text-purple-600" />
            <span className="font-medium text-purple-900">Quote Calculator</span>
            {calculatedQuote && (
              <span className="text-sm font-bold text-green-600">
                = {formatCurrency(calculatedQuote.total)}
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp size={18} className="text-purple-600" />
          ) : (
            <ChevronDown size={18} className="text-purple-600" />
          )}
        </button>

        {/* Calculator Body */}
        {isExpanded && (
          <div className="p-3 pt-0 space-y-3">
            
            {/* Measurement Input (for per-unit pricing) */}
            {needsMeasurement && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {serviceType.measurementLabel || 'Measurement'} *
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="number"
                      value={measurementValue}
                      onChange={handleMeasurementChange}
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault();
                        }
                      }}
                      className="input pr-16"
                      placeholder={
                        template.includes('sqft') || template.includes('sq') ? '1800' :
                        template.includes('linear') ? '200' :
                        template.includes('unit') || template.includes('item') ? '24' : '2'
                      }
                      min="0"
                      step={template.includes('hour') ? '0.5' : '1'}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      {template.includes('sqft') || template.includes('sq') ? 'sq ft' :
                       template.includes('linear') ? 'linear ft' :
                       template.includes('unit') || template.includes('item') ? 'items' : 'hours'}
                    </span>
                  </div>
                  
                  {/* SatMeasure Button - only for sqft/linear measurements */}
                  {canUseSatMeasure && (
                    <button
                      type="button"
                      onClick={() => setShowSatMeasure(true)}
                      className="p-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1.5 flex-shrink-0"
                      title="Measure with satellite imagery"
                    >
                      <MapPin size={18} />
                      <span className="hidden sm:inline text-sm font-medium">Measure</span>
                    </button>
                  )}
                </div>
                
                {/* Where the number came from */}
                {propTarget && propValue && measurementSource === 'property' && measurementValue && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <MapPin size={12} />
                    From property record: {propValue.toLocaleString()} {propTarget.unit}
                    {propSource && (
                      <span className="text-gray-500">
                        {' '}({propSource === 'measured' ? 'measured' : propSource === 'estimate' ? 'estimate, tap Measure to confirm' : 'entered by hand'}
                        {propDate ? `, ${new Date(propDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : ''})
                      </span>
                    )}
                  </p>
                )}
                {propTarget && !propValue && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <MapPin size={12} />
                    No {propTarget.label} size saved for this property. Measuring will save it.
                  </p>
                )}

                {/* Friendly Label Indicator - Shows matched size category */}
                {matchedFriendlyLabel && measurementValue && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                    <Lightbulb size={16} className="text-blue-600 flex-shrink-0" />
                    <p className="text-sm text-blue-800">
                      This is a <strong>{matchedFriendlyLabel}</strong> ({parseFloat(measurementValue).toLocaleString()} {template.includes('sqft') || template.includes('sq') ? 'sq ft' : template.includes('linear') ? 'linear ft' : 'units'})
                    </p>
                  </div>
                )}
                
                {/* Source indicator */}
                {measurementSource === 'satmeasure' && measurementValue && !matchedFriendlyLabel && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <MapPin size={12} />
                    Measured with SatMeasure™
                  </p>
                )}
                
                {serviceType.measurementHelpText && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <HelpCircle size={12} />
                    {serviceType.measurementHelpText}
                  </p>
                )}
                
                {/* Rate info */}
                <p className="text-xs text-purple-600 mt-1">
                  Rate: ${parseFloat(serviceType.baseRate || 0).toFixed(4)} per {serviceType.unit?.toLowerCase() || 'unit'}
                  {serviceType.minimumCharge && (
                    <span className="ml-2 text-gray-500">
                      (Min: {formatCurrency(serviceType.minimumCharge)})
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Tier Selection (for tiered pricing) */}
            {needsTierSelection && tiers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Size *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {tiers.map((tier) => (
                    <button
                      key={tier.name}
                      type="button"
                      onClick={() => setSelectedTier(tier.name)}
                      className={`p-2 rounded-lg text-center transition-all border-2 ${
                        selectedTier === tier.name
                          ? 'border-purple-500 bg-purple-100 text-purple-900'
                          : 'border-gray-200 bg-white hover:border-purple-300'
                      }`}
                    >
                      <div className="font-semibold text-sm">{tier.name}</div>
                      {tier.description && (
                        <div className="text-xs text-gray-500">{tier.description}</div>
                      )}
                      <div className="text-sm font-bold text-green-600 mt-1">
                        {formatCurrency(tier.price)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Modifiers */}
            {hasModifiers && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add-ons / Modifiers
                </label>
                <div className="space-y-2">
                  {modifiers.filter(m => m.enabled !== false).map((mod) => (
                    <label
                      key={mod.name}
                      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all border ${
                        activeModifiers[mod.name]
                          ? 'border-purple-400 bg-purple-100'
                          : 'border-gray-200 bg-white hover:border-purple-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={activeModifiers[mod.name] || false}
                          onChange={() => handleModifierToggle(mod.name)}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium text-gray-900">{mod.name}</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {mod.type === 'flat' ? `+$${mod.amount}` : `+${mod.amount}%`}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Offer to save typed values back to the property */}
            {pendingLabel && (
              <button
                type="button"
                onClick={handleSavePending}
                disabled={savingToProperty}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 underline disabled:opacity-50"
              >
                {savingToProperty ? 'Saving...' : `Save ${pendingLabel} to property`}
              </button>
            )}

            {/* Quote Summary */}
            {calculatedQuote && calculatedQuote.total > 0 && (
              <div className="bg-white border border-purple-200 rounded-lg p-3 mt-3">
                <div className="text-xs font-medium text-gray-500 uppercase mb-2">Quote Breakdown</div>
                <div className="space-y-1">
                  {calculatedQuote.breakdown.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <div>
                        <span className="text-gray-900">{item.label}</span>
                        {item.detail && (
                          <span className="text-gray-500 ml-1 text-xs">({item.detail})</span>
                        )}
                      </div>
                      <span className="font-medium">{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>
                
                {calculatedQuote.appliedMinimum && (
                  <div className="text-xs text-amber-600 mt-2 pt-2 border-t border-gray-100">
                    ⚠️ Minimum charge of {formatCurrency(calculatedQuote.minimum)} applied
                  </div>
                )}
                
                {/* Price override indicator */}
                {priceOverride && (
                  <div className="text-xs text-blue-600 mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
                    <span>📐 Rounded to ${priceOverride}</span>
                    <button
                      type="button"
                      onClick={() => setPriceOverride(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      Undo
                    </button>
                  </div>
                )}
                
                <div className="flex justify-between mt-2 pt-2 border-t border-gray-200">
                  <span className="font-bold text-gray-900">Total Quote</span>
                  <span className="font-bold text-lg text-green-600">
                    {formatCurrency(calculatedQuote.total)}
                  </span>
                </div>

                {/* Price Override - Quick round buttons + manual input */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  {!showOverride && !priceOverride ? (
                    <button
                      type="button"
                      onClick={() => setShowOverride(true)}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Override price
                    </button>
                  ) : (
                  <>
                  <p className="text-xs text-gray-500 mb-2">Override price:</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Quick round buttons */}
                    {roundPriceOptions.map((option, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleRoundToPrice(option)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          priceOverride === option.price
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 hover:bg-purple-100 text-gray-700 hover:text-purple-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}

                    {/* Manual override input */}
                    <div className="flex items-center gap-1 flex-1 min-w-[120px]">
                      <span className="text-gray-400 text-sm">$</span>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={priceOverride || ''}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          setPriceOverride(val > 0 ? val : null);
                        }}
                        onWheel={(e) => e.target.blur()}
                        placeholder="Custom"
                        className="input py-1.5 text-sm"
                      />
                    </div>

                    {/* Clear override */}
                    {priceOverride && (
                      <button
                        type="button"
                        onClick={() => setPriceOverride(null)}
                        className="text-xs text-gray-400 hover:text-gray-600 underline"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                  {priceOverride && calculatedQuote && (
                    <p className="text-xs text-blue-600 mt-1.5">
                      Calculated: {formatCurrency(calculatedQuote.breakdown.reduce((s, i) => s + i.amount, 0))} → Override: {formatCurrency(priceOverride)}
                    </p>
                  )}
                  </>
                  )}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!calculatedQuote && needsMeasurement && (
              <div className="text-center py-3 text-sm text-gray-500">
                Enter {serviceType.measurementLabel?.toLowerCase() || 'measurement'} to calculate quote
                {canUseSatMeasure && (
                  <button
                    type="button"
                    onClick={() => setShowSatMeasure(true)}
                    className="block mx-auto mt-2 text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 justify-center"
                  >
                    <MapPin size={14} />
                    Use SatMeasure™ to measure property
                  </button>
                )}
              </div>
            )}
            
            {!calculatedQuote && needsTierSelection && (
              <div className="text-center py-3 text-sm text-gray-500">
                Select a size tier to see price
              </div>
            )}
          </div>
        )}
      </div>

      {/* SatMeasure Modal */}
      <SatMeasureModal
        isOpen={showSatMeasure}
        onClose={() => setShowSatMeasure(false)}
        onMeasurementComplete={handleSatMeasureComplete}
        address={customerAddress}
        customerName={customerName}
        serviceName={serviceType?.name || ''}
        measurementType={measurementType}
      />
    </>
  );
};

export default QuoteCalculator;